import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { styled } from '@compiled/react';
import type { ComponentProps, FC, ReactNode } from 'react';
import React, { Fragment, useEffect } from 'react';

import { token } from '@atlaskit/tokens';

import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import { AnnotationsProvider } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext';
import { DocumentUpdateStatusProvider } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext';
import { FixedSpaceHeaderWithBannerContainer } from '@confluence/breadcrumbs/entry-points/Breadcrumbs';
import { useInlineCommentsContext } from '@confluence/comment-context/entry-points/InlineCommentsContext';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { ViewCommentShortcutListener } from '@confluence/comments-shortcuts/entry-points/ViewCommentShortcutListener';
import { ShareFeedbackButtonLoader } from '@confluence/company-hub-utils/entry-points/ShareFeedbackButtonLoader';
import { CompanyHubGroupedButtons } from '@confluence/company-hub/entry-points/CompanyHubGroupedButtons';
import { HubPreviewFooter } from '@confluence/company-hub/entry-points/HubPreviewFooter';
import { HubRendererWrapper } from '@confluence/company-hub/entry-points/hub-layout';
import { ContentViewedEvent } from '@confluence/content-body/entry-points/ContentViewedEvent';
import { ContentRefetchProvider } from '@confluence/content-body/entry-points/ContentRefetchContext';
import { COVERPICTUREWIDTH } from '@confluence/content-topper/entry-points/contentTopperConstants';
import { useTitleContentPropertiesForPublishedPage } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage';
import { ContentTypesHeader } from '@confluence/content-types-header/entry-points/ContentTypesHeader';
import { ReadingAidsAcronymsHighlighter } from '@confluence/contextual-reading-aids/entry-points/readingAidsAcronymsHighlighter';
import { useCreateSpaceExperiment } from '@confluence/create-space/entry-points/createSpace';
import {
	CUSTOM_HEADER_FOOTER_TYPES,
	CustomHeaderFooterLoader,
} from '@confluence/custom-header-footer';
import { AudienceSegmentationContextProvider } from '@confluence/custom-sites-extensions/entry-points/AudienceSegmentationContext';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle';
import { getEmojiFromPublishedPageProperties } from '@confluence/emoji-title/entry-points/transformer';
import { OBJECT_HEADER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component';
import { expVal, expValEquals, UNSAFE_noExposureExp } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { InlineCommentsHighlighterLoader } from '@confluence/inline-comments/entry-points/InlineCommentsHighlighterLoader';
import { InlineCommentsLoader } from '@confluence/inline-comments/entry-points/InlineCommentsLoader';
import { LoadingPriority } from '@confluence/loadable';
import { ObjectSidebarControl } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarControl';
import { usePageContentId } from '@confluence/page-context';
import { RendererPageCoverPicture } from '@confluence/page-cover-picture/entry-points/RendererPageCoverPicture';
import { getRendererCoverPictureInfo } from '@confluence/page-cover-picture/entry-points/coverPictureHelperFunctions';
import { QuickReload } from '@confluence/quick-reload/entry-points/QuickReload';
import { PageMode } from '@confluence/quick-reload/entry-points/pubSubConstants';
import { RendererActionsProvider } from '@confluence/renderer-actions/entry-points/RendererActionsContext';
import {
	createSingleQueryParamHook,
	useRouteDataRef,
} from '@confluence/route-manager/entry-points/RouteState';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { isRovoSystemSpaceKey } from '@confluence/route-manager/entry-points/isRovoSystemSpaceKey';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { SpaceOverviewQuery } from '@confluence/space-overview-query';
import { CommentsDataBackgroundLoader } from '@confluence/comments-data-manager/entry-points/CommentsDataBackgroundLoader';
import { useViewPageParams } from '@confluence/view-page-common/entry-points/useViewPageParams';
import { ViewPageContent } from '@confluence/view-page-common/entry-points/ViewPageContent';
import { ViewPageContainer } from '@confluence/view-page-common/entry-points/ViewPageContainer';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations';
import { useConnectAppsData } from '@confluence/web-panel-location/entry-points/useConnectAppsData';
import { AnnouncementBanner } from '@confluence/experiment-land-in-sample-space-xflow/entry-points/AnnouncementBanner';
import { useIsEligibleLandInSampleSpaceXflow } from '@confluence/experiment-land-in-sample-space-xflow/entry-points/useIsEligibleLandInSampleSpaceXflow';

import { SpaceHeading } from './SpaceHeader';
import { SpaceOverviewGroupedButtons } from './SpaceOverviewGroupedButtons';
import { StartSpaceOverviewPageLoad } from './perf.config';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ContentBodyStyledComponent = styled.div<{
	isCompanyHub: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:global': {
		/* Fixes padding for overview tab in non fallback mode. It relies on a global selector to avoid breaking theming */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.wiki-content': {
			padding: '28px 0px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		iframe: {
			marginTop: token('space.negative.250'),
		},
	},
	paddingTop: 0,
	paddingRight: `${token('space.500')}`,
	paddingBottom: `${token('space.250')}`,
	paddingLeft: `${token('space.500')}`,
	// added in for cover image
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	'--full-cover-image-margin': ({ isCompanyHub }) => (isCompanyHub ? '-42px' : '-40px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	'--full-cover-image-width': ({ isCompanyHub }) =>
		isCompanyHub ? 'calc(100% + 82px)' : 'calc(100% + 80px)',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles
	...({ isCompanyHub }) =>
		isCompanyHub && {
			paddingRight: 0,
			paddingLeft: 0,
			margin: '0 auto',
		},
});

export type SpaceOverviewProps = {
	spaceKey: string;
	children?: ReactNode;
};

const useFocusedCommentIdQuery = createSingleQueryParamHook('focusedCommentId');

export const SpaceOverview: FC<SpaceOverviewProps> = ({ spaceKey, children }) => {
	// Land in sample space experiment
	const { xflowSpaceCreated, loading } = useIsEligibleLandInSampleSpaceXflow();
	const shouldShowExperiment = xflowSpaceCreated && !loading;
	// eslint-disable-next-line confluence-feature-gating/unsafe-no-exposure
	const [teamAndContainerM2ExpConfig] = UNSAFE_noExposureExp(
		'edit-team-connection-from-confluence',
	);
	const { versionOverride } = useViewPageParams();
	const focusedCommentId = useFocusedCommentIdQuery();
	const { isSpaceTypesCreationFlowEnabled } = useCreateSpaceExperiment();
	const { locale, cloudId, orgId } = useSessionData();

	// Used to clear existing comments data when we leave the space overview page
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { clearCommentsData } = useCommentsDataActions();

	const routeDataRef = useRouteDataRef();

	// For Nav 3, we expand atl.general panel to full width when the side nav is not present. This is the case for
	// company hub and when url has mode=global, such as on custom homepage
	// For Nav 4, the side nav is always present, so we have an additional check on isNav4Enabled in FixedSpaceHeaderContainer
	const isFullWidth = routeDataRef.current?.queryParams?.mode === 'global';

	const [contextContentId] = usePageContentId();
	const { shouldDisplaySidebar } = useInlineCommentsContext();
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId: contextContentId,
	});

	// @ts-ignore FIXME: `contextContentId` can be `undefined` here, and needs proper handling
	const contentId: string = contextContentId;

	const panelsModuleKeysAtlGeneral = useConnectAppsData({
		contentId,
		location: ATL_GENERAL,
	});
	const panelsModuleKeysAtlFooter = useConnectAppsData({
		contentId,
		location: ATL_FOOTER,
	});

	const isReadingAidsAutoHighlightEnabled =
		(isAIEnabledForContent || isRovoEnabledForContent) && locale.startsWith('en');

	// coverPictureWidth will be cleaned up once force-fixed width flag is cleaned up
	const { coverPictureWidth } = useTitleContentPropertiesForPublishedPage({
		contentId,
	});
	const isFixedWidth = coverPictureWidth === COVERPICTUREWIDTH.FIXED;

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

	const isFixedWidthImageOption = !isCompanyHub && isFixedWidth;

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const spaceOverviewQueryResult = useQuery(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		SpaceOverviewQuery,
		{
			variables: {
				spaceKey,
				excludeOperations: Boolean(!isCompanyHub),
			},
		},
	);

	useEffect(() => {
		// We need to clear existing comments data when we leave the space overview page
		return () => {
			if (fg('confluence-frontend-comments-panel')) {
				resetInitialDataLoadedMap();
				clearCommentsData();
			}
		};
	}, [clearCommentsData, resetInitialDataLoadedMap]);

	const { data } = spaceOverviewQueryResult;
	const spaceId = data?.space?.id || '';
	const spaceName = data?.space?.name || '';
	const isThemed = data?.space?.theme?.themeKey || false;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && isThemed) {
		return null;
	}

	// dont want the function to run if the FG is off
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (fg('aifc_create_enabled') && isRovoSystemSpaceKey(spaceKey)) {
		return null;
	}

	// The feature Company Hub uses a special space type "system" which is mostly
	// hidden from the UI and the user predominantly sees the space homepage /
	// overview presented with feature-specific customizations and referred to as
	// "front cover" or "landing page". Because the space is hidden from the user,
	// the UI concept "space icon" doesn't make sense.
	const spaceIconPath = isCompanyHub ? undefined : data?.space?.icon?.path;
	const emoji = getEmojiFromPublishedPageProperties(data?.space?.homepage);

	const isSpaceIconEnabled = isSpaceTypesCreationFlowEnabled && !!spaceIconPath;

	const contentHeaderProps: ComponentProps<typeof CompanyHubGroupedButtons> &
		ComponentProps<typeof SpaceOverviewGroupedButtons> = {
		contentId,
		isThemed,
		spaceIconPath,
		spaceKey,
		spaceName,
		spaceOverviewQueryResult,
		versionOverride,
	};
	let contentHeader;
	if (isCompanyHub) {
		contentHeader = null;
	} else {
		contentHeader = fg('confluence_frontend_content_wrapper') ? (
			<FixedSpaceHeaderWithBannerContainer
				isThemed={isThemed}
				isFullWidth={isFullWidth}
				banner={shouldShowExperiment && <AnnouncementBanner spaceKey={spaceKey} />}
			>
				<ExperienceStart
					id={contentId}
					name={OBJECT_HEADER_EXPERIENCE}
					attributes={{ isSpaceOverview: true }}
				/>
				<ContentTypesHeader contentId={contentId} isSpaceOverviewPage />
			</FixedSpaceHeaderWithBannerContainer>
		) : (
			<SpaceOverviewGroupedButtons {...contentHeaderProps} />
		);
	}

	const companyHubFooter = versionOverride ? (
		<HubPreviewFooter versionOverride={versionOverride} contentId={contentId} />
	) : (
		<ShareFeedbackButtonLoader />
	);

	const spaceOverviewInternal = (
		<RendererActionsProvider>
			<>
				<StartSpaceOverviewPageLoad spaceKey={spaceKey} />
				{isCompanyHub && <CompanyHubGroupedButtons {...contentHeaderProps} />}
				<ViewPageContainer
					spaceKey={spaceKey}
					contentId={contentId}
					stickyHeader={contentHeader}
					isContainerContentView={!isCompanyHub}
					versionOverride={isCompanyHub ? versionOverride : null}
				>
					{({ contentQueryData, contentQueryError, isThemed }) => {
						const isFabricPage = Boolean(
							contentQueryData?.content?.nodes?.[0]?.body?.dynamic?.representation ===
								'atlas_doc_format',
						);

						const shouldRenderInlineComments = !isCompanyHub;

						const shouldRenderInlineCommentsHighlighterLoader = expVal(
							'cc_comments_new_comments_data_manager',
							'isEnabled',
							false,
						)
							? shouldRenderInlineComments && !isFabricPage
							: shouldRenderInlineComments;

						const {
							coverPicture,
							contentAppearance,
							hasCoverPicture,
							coverPictureWidth,
							isTitleCenterAligned,
						} = getRendererCoverPictureInfo(contentQueryData, false);
						const isPageContentFullWidth = contentAppearance?.published === 'full-width';
						const hasEmoji = !!emoji || isSpaceIconEnabled;
						const isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji =
							!isTitleCenterAligned &&
							isFixedWidthImageOption &&
							isPageContentFullWidth &&
							hasCoverPicture;

						return (
							<>
								{/* Body of content */}
								{/* Space Overview NVP non-theme uses contentHeader inside of ViewPageContainer.
									Because contentHeader is disabled when themes are enabled, need to display the
									header outside of content to match Space Overview Classic
								*/}
								{isThemed && contentHeader}
								{!isCompanyHub && (
									<CustomHeaderFooterLoader type={CUSTOM_HEADER_FOOTER_TYPES.HEADER} />
								)}
								{!isCompanyHub && (
									<DocumentContentTitle
										contentTitle={contentQueryData?.content?.nodes?.[0]?.title || ''}
										contentSpace={spaceName}
									/>
								)}
								<ContentBodyStyledComponent
									isCompanyHub={isCompanyHub}
									data-testid="space-overview-content"
								>
									{hasCoverPicture && !isCompanyHub && (
										<RendererPageCoverPicture
											contentId={contentId}
											isPagePreview={false}
											coverPicture={coverPicture}
											contentAppearance={contentAppearance}
											isSpaceOverview
											hasEmoji={hasEmoji}
											coverPictureWidthOverride={coverPictureWidth}
										/>
									)}
									{!isCompanyHub && (
										<SpaceHeading
											{...{
												emoji,
												title: contentQueryData?.content?.nodes?.[0]?.title,
												isTitleCenterAligned,
												isCompanyHub,
												contentAppearance: contentAppearance?.published,
												hasCoverPicture,
												spaceIconPath,
												isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji,
												isPageContentFullWidth,
												isFixedWidthImageOption,
												cloudId,
												spaceId,
												spaceKey,
												...(teamAndContainerM2ExpConfig.get('isEnabled', false) && { orgId }),
											}}
										/>
									)}
									{shouldRenderInlineCommentsHighlighterLoader && (
										<InlineCommentsHighlighterLoader pageId={contentId} />
									)}
									{isReadingAidsAutoHighlightEnabled && (
										<ReadingAidsAcronymsHighlighter contentId={contentId} />
									)}
									<DocumentUpdateStatusProvider>
										<ContentRefetchProvider>
											<AnnotationsProvider>
												<ViewPageContent
													contentId={contentId}
													data={contentQueryData}
													error={contentQueryError}
													spaceKey={spaceKey}
													hasInlineComments={shouldRenderInlineComments}
													isSpaceOverview
													customPerfData={{
														webPanelsAtlGeneral: panelsModuleKeysAtlGeneral,
														webPanelsAtlFooter: panelsModuleKeysAtlFooter,
													}}
												/>
											</AnnotationsProvider>
										</ContentRefetchProvider>
									</DocumentUpdateStatusProvider>
									{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
									{!process.env.REACT_SSR && (
										<Fragment>
											<ContentViewedEvent contentId={contentId} />
											{expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) && (
												<CommentsDataBackgroundLoader contentId={contentId} />
											)}
										</Fragment>
									)}
									{!isCompanyHub && (
										<CustomHeaderFooterLoader type={CUSTOM_HEADER_FOOTER_TYPES.FOOTER} />
									)}
									<QuickReload
										contentId={contentId}
										contentType={contentQueryData?.content?.nodes?.[0]?.type}
										// The 'Date.now()' is expedient; see notes in quick-reload
										// package on what a more correct lastPollTime value would be.
										lastPollTime={Date.now()}
										pageMode={PageMode.RENDERER}
									/>
									{children}
								</ContentBodyStyledComponent>
								{shouldRenderInlineComments && shouldDisplaySidebar && (
									<InlineCommentsLoader
										pageId={contentId}
										isFabricPage={isFabricPage}
										loadingPriority={
											window.__SSR_RENDERED__ && focusedCommentId
												? LoadingPriority.AFTER_PAINT
												: LoadingPriority.BACKGROUND
										}
										focusedCommentId={focusedCommentId}
									/>
								)}
								<ViewCommentShortcutListener isFabricPage={isFabricPage} contentId={contentId} />
								{!isCompanyHub && (
									<ObjectSidebarControl
										hasCommentsButton
										hasDetailsButton
										hasAiButton={
											fg('aifc_create_enabled') ||
											expValEquals(
												'rovo_chat_refresh_v1_experiment',
												'isRenderRefreshEnabled',
												true,
											)
										}
										contentId={contentId}
										spaceKey={spaceKey}
									/>
								)}
								{isCompanyHub && companyHubFooter}
							</>
						);
					}}
				</ViewPageContainer>
			</>
		</RendererActionsProvider>
	);

	let spaceOverviewWithWrappers = spaceOverviewInternal;

	if (isCompanyHub) {
		spaceOverviewWithWrappers = (
			<HubRendererWrapper>
				<AudienceSegmentationContextProvider isCompanyHub shouldFetchUserGroups>
					{spaceOverviewWithWrappers}
				</AudienceSegmentationContextProvider>
			</HubRendererWrapper>
		);
	}

	return spaceOverviewWithWrappers;
};
